import React, { useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import styles from './PreferencePicker.module.css';

function normalizeOption(option) {
  if (option && typeof option === 'object') {
    return { value: String(option.value), label: String(option.label || option.value) };
  }
  const value = String(option);
  return { value, label: value };
}

function SelectedChip({ label, onRemove, disabled }) {
  return (
    <span className={styles.chip}>
      <span className={styles.chipLabel}>{label}</span>
      <button
        type="button"
        className={styles.chipRemove}
        onClick={onRemove}
        disabled={disabled}
        aria-label={`Remove ${label}`}
      >
        ×
      </button>
    </span>
  );
}

export default function PreferencePicker({
  id,
  addLabel = '+ Add',
  searchLabel,
  values = [],
  options = [],
  onChange,
  allowCustom = false,
  suggestions = [],
  limit,
  disabled = false,
  placeholder = 'Search',
  emptyMessage = 'No matches',
  onLimit,
  mode = 'multi',
  value = '',
  onSelect,
}) {
  const reactId = useId();
  const listId = `${id || 'picker'}-list-${reactId}`;
  const inputId = id || `picker-${reactId}`;
  const wrapRef = useRef(null);
  const inputRef = useRef(null);
  const menuRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);

  const selected = useMemo(() => (mode === 'single' ? (value ? [value] : []) : values.filter(Boolean)), [mode, value, values]);
  const selectedSet = useMemo(() => new Set(selected.map((v) => String(v).toLowerCase())), [selected]);
  const optionList = useMemo(() => options.map(normalizeOption), [options]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const remaining = optionList.filter((opt) => {
      if (mode === 'multi' && selectedSet.has(opt.value.toLowerCase())) return false;
      if (!q) return true;
      return opt.label.toLowerCase().includes(q) || opt.value.toLowerCase().includes(q);
    });
    if (allowCustom) {
      const custom = query.trim();
      if (custom.length > 1 && !selectedSet.has(custom.toLowerCase())) {
        const exact = remaining.some((opt) => opt.label.toLowerCase() === custom.toLowerCase());
        if (!exact) {
          return [{ value: custom, label: `Add “${custom}”`, custom: true }, ...remaining];
        }
      }
    }
    return remaining;
  }, [allowCustom, mode, optionList, query, selectedSet]);

  const atLimit = typeof limit === 'number' && mode === 'multi' && selected.length >= limit;

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (event) => {
      if (wrapRef.current && !wrapRef.current.contains(event.target)) {
        setOpen(false);
        setQuery('');
      }
    };
    const onKey = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        setOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  useEffect(() => {
    if (open) {
      setActiveIndex(0);
      const t = window.setTimeout(() => inputRef.current?.focus(), 0);
      return () => window.clearTimeout(t);
    }
    return undefined;
  }, [open]);

  useLayoutEffect(() => {
    if (!open || !menuRef.current) return;
    const el = menuRef.current;
    const rect = el.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.top - 12;
    const spaceAbove = rect.top - 12;
    const max = Math.min(240, Math.max(120, spaceBelow));
    if (spaceBelow < 140 && spaceAbove > spaceBelow) {
      el.style.top = 'auto';
      el.style.bottom = 'calc(100% + 6px)';
      el.style.maxHeight = `${Math.min(240, Math.max(120, spaceAbove))}px`;
    } else {
      el.style.top = 'calc(100% + 6px)';
      el.style.bottom = 'auto';
      el.style.maxHeight = `${max}px`;
    }
  }, [open, filtered.length]);

  const commit = (nextValue) => {
    const next = String(nextValue || '').trim();
    if (!next) return;
    if (mode === 'single') {
      onSelect?.(next);
      setOpen(false);
      setQuery('');
      return;
    }
    if (selectedSet.has(next.toLowerCase())) {
      setOpen(false);
      setQuery('');
      return;
    }
    if (atLimit) {
      onLimit?.();
      return;
    }
    onChange?.([...selected, next]);
    setQuery('');
    setActiveIndex(0);
  };

  const removeAt = (item) => {
    if (mode === 'single') {
      onSelect?.('');
      return;
    }
    onChange?.(selected.filter((v) => v !== item));
  };

  const onInputKeyDown = (event) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((i) => Math.min(filtered.length - 1, i + 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((i) => Math.max(0, i - 1));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const choice = filtered[activeIndex];
      if (choice) commit(choice.value);
      else if (allowCustom && query.trim().length > 1) commit(query.trim());
    } else if (event.key === 'Escape') {
      event.preventDefault();
      setOpen(false);
      setQuery('');
    }
  };

  const combo = (
    <div className={styles.combo} ref={wrapRef}>
      <input
        id={inputId}
        ref={inputRef}
        className={styles.input}
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={filtered[activeIndex] ? `${listId}-${activeIndex}` : undefined}
        aria-label={searchLabel || addLabel}
        placeholder={placeholder}
        value={query}
        disabled={disabled}
        autoComplete="off"
        onChange={(e) => {
          setQuery(e.target.value);
          setActiveIndex(0);
          if (!open) setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={onInputKeyDown}
      />
      {open ? (
        <ul className={styles.menu} id={listId} role="listbox" ref={menuRef}>
          {filtered.length === 0 ? (
            <li className={styles.empty} role="presentation">
              {emptyMessage}
            </li>
          ) : (
            filtered.map((opt, index) => (
              <li
                key={`${opt.value}-${index}`}
                id={`${listId}-${index}`}
                role="option"
                aria-selected={index === activeIndex}
                className={`${styles.option} ${index === activeIndex ? styles.optionActive : ''}`}
                onMouseEnter={() => setActiveIndex(index)}
                onMouseDown={(e) => {
                  e.preventDefault();
                  commit(opt.value);
                }}
              >
                <span className={styles.optionLabel}>{opt.custom ? opt.label : opt.label}</span>
                {mode === 'single' && value === opt.value ? <span className={styles.check} aria-hidden="true">✓</span> : null}
              </li>
            ))
          )}
        </ul>
      ) : null}
    </div>
  );

  if (mode === 'single') {
    return (
      <div className={styles.picker}>
        {open ? (
          combo
        ) : (
          <button
            type="button"
            className={styles.singleBtn}
            disabled={disabled}
            aria-haspopup="listbox"
            aria-expanded={false}
            onClick={() => setOpen(true)}
          >
            <span className={value ? styles.singleValue : styles.singlePlaceholder}>{value || placeholder}</span>
            <span className={styles.chevron} aria-hidden="true" />
          </button>
        )}
      </div>
    );
  }

  return (
    <div className={styles.picker}>
      <div className={styles.row}>
        {selected.map((item) => (
          <SelectedChip key={item} label={item} disabled={disabled} onRemove={() => removeAt(item)} />
        ))}
        {atLimit ? null : open ? (
          combo
        ) : (
          <button type="button" className={styles.addBtn} disabled={disabled} onClick={() => setOpen(true)}>
            {addLabel}
          </button>
        )}
      </div>
      {selected.length === 0 && !open && suggestions.length > 0 ? (
        <div className={styles.suggestions}>
          <span className={styles.suggestLabel}>Suggested</span>
          {suggestions.map((item) => (
            <button
              key={item}
              type="button"
              className={styles.suggestBtn}
              disabled={disabled}
              onClick={() => {
                if (atLimit) {
                  onLimit?.();
                  return;
                }
                onChange?.([...selected, item]);
              }}
            >
              {item}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
